import classnames from 'classnames'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectIsCorrespondingPageAdConfigLoaded, selectPageIdForAdConfig } from '../../store/ad/ad.selectors'
import { removeAdData, setAdData } from '../../store/ad/adSlice'
import { noop } from '../../utils/function'
import { withEmptyFallbackErrorBoundary } from '../error/EmptyFallbackErrorBoundary'
import { ONE_SECOND_MS } from '../utils/date'
import useTimeout from '../utils/useTimeout'
import { getAdIdByAdName } from './AdConfigService'
import s from './AdLoader.less'
import { AdService } from './AdService'

export const AD_STATUS_NONE = 'AD_STATUS_NONE'
export const AD_STATUS_LOADING = 'AD_STATUS_LOADING'
export const AD_STATUS_LOADED = 'AD_STATUS_LOADED'

export const useAdLoader = ({ adId, keywords, keywordsSelector = noop, askData = false }) => {
  const [adStatus, setAdStatus] = useState(AD_STATUS_NONE)
  const dispatch = useDispatch()
  const dynamicKeywords = useSelector(keywordsSelector)
  const adsKeywords = dynamicKeywords ?? keywords

  useEffect(() => {
    // keywords selectors must return null if there is no keywords available yet
    if (dynamicKeywords === null || keywords === null || !adId) return

    switch (adStatus) {
      case AD_STATUS_NONE: {
        setAdStatus(AD_STATUS_LOADING)

        AdService.createAd(adId)
          .then(() => AdService.setAdKeywords(adId, adsKeywords))
          .then(() => AdService.loadAd(adId))
          .then(() => askData && AdService.getAdData(adId).then(data => dispatch(setAdData({ adId, data }))))
          .then(() => setAdStatus(AD_STATUS_LOADED))
          .catch(error => console.warn('AdLoader loading error', error))
        break
      }

      case AD_STATUS_LOADED: {
        setAdStatus(AD_STATUS_LOADING)
        dispatch(removeAdData(adId))

        AdService.setAdKeywords(adId, adsKeywords)
          .then(() => AdService.reloadAd(adId))
          .then(() => askData && AdService.getAdData(adId).then(data => dispatch(setAdData({ adId, data }))))
          .then(() => setAdStatus(AD_STATUS_LOADED))
          .catch(error => console.warn('AdLoader reload', error))

        break
      }
    }
  }, [JSON.stringify(adsKeywords)])

  useEffect(() => {
    // sometimes, the ad api does resolve or reject the promise, so we do not keep loading status more than 5s, because it blocks refresh
    let timeoutId
    if (adStatus === AD_STATUS_LOADING) {
      timeoutId = setTimeout(() => setAdStatus(AD_STATUS_LOADED), 5 * ONE_SECOND_MS)
    }
    return () => clearTimeout(timeoutId)
  }, [adStatus])

  useEffect(
    () => () => {
      if (adId) {
        AdService.deleteAd(adId).catch(error => console.warn('AdLoader remove error', error))
        dispatch(removeAdData(adId))
      }
    },
    []
  )

  return adStatus === AD_STATUS_LOADED
}

export const BaseAdLoader = ({
  adName,
  className,
  keywords,
  keywordsSelector,
  children,
  askData,
  placeHolderHeight
}) => {
  const pageId = useSelector(selectPageIdForAdConfig)
  const adId = getAdIdByAdName(adName, pageId)
  const [visible, setVisible] = useState(AdService.isAdLibraryAvailable())

  useAdLoader({ adId, keywords, keywordsSelector, askData })

  const [pendingLoadTimeout, setPendingLoadTimeout] = useState(true)
  const [setTimeout] = useTimeout(2500) // delay to let the ad load
  useEffect(() => {
    setTimeout(() => {
      setPendingLoadTimeout(false)
      setVisible(AdService.isAdLibraryAvailable())
    })
  }, [setPendingLoadTimeout])

  if (!visible) return <></> // if adblocker

  return (
    // placeholder until ad is loaded
    <div style={pendingLoadTimeout ? { minHeight: placeHolderHeight } : {}}>
      {children}
      <div id={adId} className={classnames(className, askData && s.hidden)} />
    </div>
  )
}

export default withEmptyFallbackErrorBoundary(function AdLoader({ adName, placeHolderHeight = 0, ...restProps }) {
  const isConfigLoaded = useSelector(state => selectIsCorrespondingPageAdConfigLoaded(state, adName))

  if (!isConfigLoaded) return <div style={{ minHeight: placeHolderHeight }}></div> // placeholder until page configuration is loaded

  return <BaseAdLoader key={adName} adName={adName} placeHolderHeight={placeHolderHeight} {...restProps} />
})
